import { RNG } from "rot-js";
import { Actor } from "../actors/Actor";
import { BreedTypes } from "../actors/breeds";
import EntityManager from "../EntityManager";
import { gameData } from "../GameManager";
import {
  carveShared,
  carveZDifference,
  makeDoor,
  makeRect,
  makeZRamp,
} from "./carving/Carving";
import { BSPGenerate } from "./graph/BSPGenerator";
import { GraphNode } from "./graph/buildMinSpanTree";
import { placeMonsters } from "./placement/MonsterPlacement";
import { placeShrines } from "./placement/ShrinePlacement";
import { ShrineMap } from "./ShrineMap";

export const generateMap = (
  rng: typeof RNG,
  map: ShrineMap,
  player?: Actor
) => {
  const rooms = BSPGenerate(rng, map.width - 1, map.height - 1);
  map.generate(rng);
  let room: GraphNode;
  for (room of rooms.values()) {
    makeRect(map, room.bounds, room.z);
  }

  let index: number;
  for ([index, room] of rooms.entries()) {
    room.adj.forEach((idx) => {
      if (index < idx) {
        const adjRoom = rooms.get(idx);
        if (adjRoom) {
          if (room.z !== adjRoom.z) {
            carveZDifference(
              map,
              room.bounds,
              adjRoom.bounds,
              adjRoom.z < room.z
            );
          } else if (rng.getUniformInt(0, 1)) {
            carveShared(map, room.bounds, adjRoom.bounds);
          } else {
            makeDoor(rng, map, room.bounds, adjRoom.bounds);
          }
        }
      }
    });
  }

  connectZLevels(rng, map, rooms);

  const [oldPos, startingRoom] = placeShrines(rng, map, rooms);
  if (player) {
    player.x = oldPos.x;
    player.y = oldPos.y;
  } else {
    gameData.player = EntityManager.createActor(
      BreedTypes.player,
      oldPos.x,
      oldPos.y
    );
  }
  placeMonsters(rng, map, rooms, startingRoom);
};

const connectZLevels = (
  rng: typeof RNG,
  map: ShrineMap,
  rooms: Map<number, GraphNode>
) => {
  const start = rooms.keys().next().value;
  const queued: boolean[] = [];
  const visited: boolean[] = [];
  const horizon: number[] = [start];
  queued[start] = true;

  let room: GraphNode | undefined;
  let next: number;
  while (0 < horizon.length) {
    next = horizon.pop() as number;
    if (!visited[next]) {
      room = rooms.get(next);
      if (room) {
        if (1 < room.z) {
          const platformAdjs: [number, number][] = [];
          let platformCurIdx: number, fromIdx: number | null;
          let platformCur;
          const platformQueue: [number, number | null][] = [[next, null]];
          while (0 < platformQueue.length) {
            [platformCurIdx, fromIdx] = platformQueue.pop() as [
              number,
              number | null
            ];
            platformCur = rooms.get(platformCurIdx);

            if (platformCur) {
              if (room.z === platformCur.z) {
                visited[platformCurIdx] = true;
                queued[platformCurIdx] = true;
                platformCur.adj.forEach((index) => {
                  if (!visited[index]) {
                    platformQueue.push([index, platformCurIdx]);
                  }
                });
              } else {
                if (!visited[platformCurIdx] && !queued[platformCurIdx]) {
                  queued[platformCurIdx] = true;
                  horizon.push(platformCurIdx);
                }
                if (fromIdx) {
                  platformAdjs.push([platformCurIdx, fromIdx]);
                }
              }
            }
          }
          const targetConnection = platformAdjs.length
            ? rng.getItem(platformAdjs)
            : ([rng.getItem(room.adj) as number, next] as const);
          if (targetConnection) {
            const a = rooms.get(targetConnection[0]);
            const b = rooms.get(targetConnection[1]);
            if (a && b) {
              makeZRamp(map, a.bounds, b.bounds, false);
            }
          } else {
            console.warn("No target connection found");
          }
        } else {
          //Ground level, just queue all unseen adjacent
          room.adj.forEach((adj) => {
            if (!visited[adj] && !queued[adj]) {
              queued[adj] = true;
              horizon.push(adj);
            }
          });
        }
      } else {
        console.warn("Room not found for z-diff");
      }
    }
  }
};
